if (!customElements.get('personalise-bundle-form')) {
  class PersonaliseBundleForm extends HTMLElement {
    constructor() {
      super();

      this.section = this.closest('.shopify-section') || this.closest('.js-product');
      this.data = this.getBundleData();
      this.variantMap = this.buildVariantMap();
      this.variantRequestId = 0;
      this.addonInputs = this.querySelectorAll('.js-personalise-addon');

      this.addEventListener('change', this.handleAddonChange.bind(this));
      this.updateAddonAvailability();
      this.applyVariant(this.getMatchedVariant(), { fetch: false, updateUrl: false });
    }

    getBundleData() {
      const dataEl = this.querySelector('[data-personalise-bundle-data]');
      if (!dataEl) return { baseTokens: [], variants: [] };
      try {
        return JSON.parse(dataEl.textContent);
      } catch (error) {
        console.log(error);
        return { baseTokens: [], variants: [] };
      }
    }

    tokenKey(tokens) {
      return (tokens || [])
        .map((token) => String(token).trim().toLowerCase())
        .filter(Boolean)
        .sort()
        .join('\0');
    }

    buildVariantMap() {
      const map = new Map();
      (this.data.variants || []).forEach((variant) => {
        map.set(this.tokenKey(variant.tokens), variant);
      });
      return map;
    }

    getSelectedAddonTokens() {
      return Array.from(this.addonInputs)
        .filter((input) => input.checked && !input.disabled)
        .map((input) => input.value);
    }

    getMatchedVariant() {
      const tokens = [...(this.data.baseTokens || []), ...this.getSelectedAddonTokens()];
      return this.variantMap.get(this.tokenKey(tokens)) || null;
    }

    handleAddonChange(evt) {
      if (!evt.target.classList.contains('js-personalise-addon')) return;

      const variant = this.getMatchedVariant();
      if (!variant) {
        evt.target.checked = !evt.target.checked;
        this.updateAddonAvailability();
        return;
      }

      this.updateAddonAvailability();
      this.applyVariant(variant, { fetch: true, updateUrl: true });
    }

    updateAddonAvailability() {
      const selectedAddons = this.getSelectedAddonTokens();

      this.addonInputs.forEach((input) => {
        const trialAddons = input.checked
          ? selectedAddons
          : [...selectedAddons, input.value];
        const trialTokens = [...(this.data.baseTokens || []), ...trialAddons];
        const variant = this.variantMap.get(this.tokenKey(trialTokens));
        const isAvailableCombo = Boolean(variant);

        input.classList.toggle('is-unavailable', !isAvailableCombo);
        if (!input.checked) {
          input.disabled = !isAvailableCombo;
        } else {
          input.disabled = false;
        }
      });
    }

    applyVariant(variant, { fetch = true, updateUrl = true } = {}) {
      this.variant = variant;
      this.updateIdInput(variant);
      this.updateAddToCartButton(variant);

      if (updateUrl) this.updateUrl(variant);

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.querySelector('.js-product-form'),
          variant,
          selectedOptions: variant ? variant.tokens : []
        }
      }));

      if (fetch && variant) this.fetchVariantContent(variant);
    }

    updateIdInput(variant) {
      this.querySelectorAll('.js-product-form input[name="id"]').forEach((input) => {
        input.value = variant ? variant.id : '';
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateAddToCartButton(variant) {
      const addBtn = this.querySelector('[name="add"]');
      if (!addBtn) return;

      if (!variant) {
        addBtn.disabled = true;
        addBtn.textContent = theme.strings.noVariant;
        return;
      }

      if (!variant.available) {
        addBtn.disabled = true;
        addBtn.textContent = theme.strings.noStock;
        return;
      }

      addBtn.disabled = false;
      addBtn.textContent = addBtn.dataset.addToCartText;
    }

    updateUrl(variant) {
      if (this.dataset.updateUrl === 'false') return;
      const url = variant
        ? `${this.dataset.url}?variant=${variant.id}`
        : this.dataset.url;
      window.history.replaceState({}, '', url);
    }

    constructVariantUrl(variant) {
      const url = new URL(this.dataset.url, window.location.origin);
      const sectionIds = [this.dataset.sectionId];

      document.querySelectorAll('.cc-variant-dependent-section [data-section-id]').forEach((el) => {
        sectionIds.push(el.dataset.sectionId);
      });

      url.searchParams.set('sections', sectionIds.join(','));
      if (variant) url.searchParams.set('variant', variant.id);
      return url;
    }

    fetchVariantContent(variant) {
      if (!theme.fetchCache || !variant) return;

      this.variantRequestId += 1;
      const currentRequestId = this.variantRequestId;
      const url = this.constructVariantUrl(variant);

      theme.fetchCache.fetch(url.toString())
        .then((responseText) => {
          if (currentRequestId !== this.variantRequestId) return;
          const responseData = JSON.parse(responseText);

          Object.entries(responseData).forEach(([section, htmlStr]) => {
            const html = new DOMParser().parseFromString(htmlStr, 'text/html');
            const sectionId = `shopify-section-${section}`;
            let target = this.section;
            const newContent = html.getElementById(sectionId);

            if (this.section.id !== sectionId) {
              target = document.getElementById(sectionId);
            }

            this.updateContent(target, newContent);
          });
        })
        .catch((error) => {
          console.log(error);
        });
    }

    updateContent(target, newContent) {
      if (!target || !newContent) return;

      target.querySelectorAll('[data-dynamic-variant-content]').forEach((toReplace) => {
        const replaceWith = newContent.querySelector(
          `[data-dynamic-variant-content="${CSS.escape(toReplace.dataset.dynamicVariantContent)}"]`
        );
        if (!replaceWith) return;

        document.dispatchEvent(new CustomEvent('on:variant:before-replace-element', {
          bubbles: true,
          detail: { toReplace, replaceWith }
        }));

        const isIdInput = replaceWith.matches('input[name="id"]');
        if (isIdInput) {
          [...replaceWith.attributes].forEach((attr) => toReplace.setAttribute(attr.name, attr.value));
          toReplace.value = replaceWith.value;
        } else {
          toReplace.replaceWith(replaceWith);
        }
      });
    }
  }

  customElements.define('personalise-bundle-form', PersonaliseBundleForm);
}
